import { ApplicationRef, Injectable, Injector } from '@angular/core';
import { environment } from '@env';
import { BehaviorSubject, Subject } from 'rxjs';
import hcs from 'src/hc.config';
import { HCConfig, HCConfigFeatures, HCConfigRules } from '../../../hc-config/hc-config';
import { LocalStorageService } from './local-storage.service';
import { HcRemoteConfigService } from './hc-remote-config.service';

@Injectable({
  providedIn: 'root',
})
export class HcService {
  private allHcs: HCConfig[] = hcs;
  private hcConfig: HCConfig;

  private memedWindow: Window | null = null;

  private readonly _currentHcId$ = new BehaviorSubject<string | null>(null);
  public readonly currentHcId$ = this._currentHcId$.asObservable();

  // Emits whenever the remote HC config is applied, so OnPush consumers that cache a
  // config-derived value (feature flags, logo, etc.) can markForCheck/detectChanges to
  // refresh without waiting for an F5. Consumers should read the config again (getter or
  // hcService method call) when this fires instead of relying on a value cached at init.
  private readonly _configUpdated$ = new Subject<void>();
  public readonly configUpdated$ = this._configUpdated$.asObservable();

  constructor(
    private localStorageService: LocalStorageService,
    private hcRemoteConfigService: HcRemoteConfigService,
    private injector: Injector,
  ) {
    this.allHcs = this.applyE2eHcFlags(this.allHcs);
    this.init();
    this._currentHcId$.next(this.hcConfig?.hcId ?? null);
  }

  public init(): void {
    const hcFromUrl = this.getClientDomainFromUrl();
    if (hcFromUrl) {
      this.hcConfig = hcFromUrl;
      if (hcFromUrl.hcId) {
        this.localStorageService.store('healthCenterId', hcFromUrl.hcId);
      }
      return;
    }
    this.getConfigByDomain();
  }

  public async initialiseRemoteConfig(): Promise<void> {
    if ((environment as { disableRemoteHcConfig?: boolean }).disableRemoteHcConfig) {
      return;
    }

    await this.hcRemoteConfigService.listenToHealthCenters((remoteHcs) => {
      if (!remoteHcs.length) {
        return;
      }

      this.allHcs = this.mergeHealthCenters(remoteHcs);
      this.init();
      this._currentHcId$.next(this.hcConfig?.hcId ?? null);
      this._configUpdated$.next();
      // Firestore's realtime listener can fire well after bootstrap, including for components
      // using the default change detection strategy that won't otherwise refresh on their own.
      // ApplicationRef is resolved lazily (not constructor-injected) because HcService is a
      // dependency of an APP_INITIALIZER: eagerly injecting ApplicationRef here would create a
      // cyclic DI dependency (ApplicationRef -> ApplicationInitStatus -> APP_INITIALIZER -> HcService -> ApplicationRef).
      // Note: this alone does not refresh OnPush components that aren't already dirty - those
      // must subscribe to configUpdated$ themselves and call markForCheck()/detectChanges().
      this.injector.get(ApplicationRef).tick();
    });
  }

  public getHcDomain(): string {
    const hcConfig = this.getHcConfig();
    return environment.environmentVariables[hcConfig.config.domainKey] || hcConfig.config.domain || hcConfig.links.site;
  }

  public getHcReactDomain(reactDomainKeyStr?: string): string {
    const hcConfig = this.getHcConfig();
    const reactDomainKey = reactDomainKeyStr || hcConfig.config.reactDomainKey;
    return environment.environmentVariables[reactDomainKey];
  }

  public getHcConfig(): HCConfig {
    const hcId = this.localStorageService.getItem('healthCenterId');
    const token = this.localStorageService.getItem('auth');

    if (token && token !== 'null') {
      return this.getConfigById(hcId);
    } else {
      return this.getConfigByDomain();
    }
  }

  public getHcConfigByDomain(): HCConfig {
    return this.getConfigByDomain();
  }

  public hasFeature(...args: HCConfigFeatures[]) {
    return args.some((item) => this.getHcConfig().features.includes(item));
  }

  public hasRule(...args: HCConfigRules[]) {
    return args.some((item) => this.getHcConfig().rules.includes(item));
  }

  public switchHC(hcId: string) {
    const newHcConfig = this.allHcs.find((hc) => hc.hcId === hcId);
    this.hcConfig = newHcConfig || this.allHcs[0];
    this.localStorageService.store('healthCenterId', hcId);
    this.closeMemedWindow();
    this._currentHcId$.next(this.hcConfig?.hcId ?? null);
  }

  public setMemedWindow(memedWindow: Window | null): void {
    this.memedWindow = memedWindow;
  }

  private getConfigByDomain() {
    const host = window.location.host;
    const newHcConfig = this.allHcs.find((hc) => host.includes(hc.config.domain));
    this.hcConfig = newHcConfig || this.allHcs[0];
    return this.hcConfig;
  }

  private getConfigById(hcId: string) {
    const newHcConfig = this.allHcs.find((hc) => hc.hcId === hcId);
    this.hcConfig = newHcConfig || this.allHcs[0];
    return this.hcConfig;
  }

  private closeMemedWindow(): void {
    if (this.memedWindow && !this.memedWindow.closed) {
      this.memedWindow.close();
    }
    this.memedWindow = null;
  }

  private getClientDomainFromUrl(): HCConfig | null {
    try {
      const url = new URL(window.location.href);
      const ptmClientDomain = url.searchParams.get('ptmClientDomain');

      if (!ptmClientDomain) {
        return null;
      }

      const matched = this.allHcs.find((hc) => hc.config.clientDomainHeader === ptmClientDomain);

      return matched || null;
    } catch {
      return null;
    }
  }

  /**
   * E2E build only (`environment.e2e`): applies the feature/rule flags a test fixed for the
   * scenario (`I.useHcFlags`, stored in localStorage as `e2e:hc-flags`) to every HC, so a test can
   * turn a flag on or off without depending on the HC configs. Ignored in any other build.
   */
  private applyE2eHcFlags(allHcs: HCConfig[]): HCConfig[] {
    if (!(environment as { e2e?: boolean }).e2e) {
      return allHcs;
    }

    const flags = this.readE2eHcFlags();

    if (!flags) {
      return allHcs;
    }

    const apply = <T>(current: T[], enable: T[] = [], disable: T[] = []): T[] => [
      ...current.filter((item) => !disable.includes(item)),
      ...enable.filter((item) => !current.includes(item)),
    ];

    return allHcs.map((hc) => ({
      ...hc,
      features: apply(hc.features, flags.enableFeatures, flags.disableFeatures),
      rules: apply(hc.rules, flags.enableRules, flags.disableRules),
    }));
  }

  private readE2eHcFlags(): {
    enableFeatures?: HCConfigFeatures[];
    disableFeatures?: HCConfigFeatures[];
    enableRules?: HCConfigRules[];
    disableRules?: HCConfigRules[];
  } | null {
    try {
      return JSON.parse(window.localStorage.getItem('e2e:hc-flags') || 'null');
    } catch {
      return null;
    }
  }

  private mergeHealthCenters(remoteHcs: HCConfig[]): HCConfig[] {
    // Remote configs should override local configs for the same HC, while local configs remain as fallback.
    // The merge is deep so that fields the local config has but a (possibly older) remote document
    // doesn't define yet - e.g. after adding a new field to HCConfig - keep falling back to the local value
    // instead of disappearing.
    const mergedHcs = this.allHcs.map((localHc) => {
      const remoteHc = remoteHcs.find((hc) => this.isSameHealthCenter(hc, localHc));

      if (!remoteHc) {
        return localHc;
      }

      // HC ids can still be injected by environment variables at build time.
      // Preserve the local id when Firestore was imported from a config file that had hcId empty.
      return {
        ...this.deepMergeHcConfig(localHc, remoteHc),
        hcId: remoteHc.hcId || localHc.hcId,
      };
    });

    // Allows onboarding an HC only through Firestore when the document has the complete HCConfig shape.
    const remoteOnlyHcs = remoteHcs.filter(
      (remoteHc) => !mergedHcs.some((hc) => this.isSameHealthCenter(hc, remoteHc)),
    );

    return [...mergedHcs, ...remoteOnlyHcs];
  }

  // Merges nested plain objects (config, assets, dataDeletion, firstAccess...) field by field instead of
  // replacing them wholesale, so a remote document missing a field (e.g. one added locally after the
  // document was last imported) falls back to the local value instead of becoming undefined. Arrays
  // (features, rules) and primitives are replaced outright by the remote value when present. A remote
  // `null` is treated the same as `undefined` (fall back to local): Firestore documents can have a
  // nested field explicitly stored as null (e.g. a logo object not yet migrated), and HCConfig fields
  // are never meant to be null - letting it through wipes out a required local default (e.g.
  // firstAccess.logo) and crashes any template reading a property off it.
  private deepMergeHcConfig<T>(localValue: T, remoteValue: T): T {
    if (!this.isPlainObject(localValue) || !this.isPlainObject(remoteValue)) {
      return remoteValue !== undefined && remoteValue !== null ? remoteValue : localValue;
    }

    const merged: Record<string, unknown> = { ...localValue };

    for (const key of Object.keys(remoteValue)) {
      merged[key] = this.deepMergeHcConfig(
        (localValue as Record<string, unknown>)[key],
        (remoteValue as Record<string, unknown>)[key],
      );
    }

    return merged as T;
  }

  private isPlainObject(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  private isSameHealthCenter(firstHc: HCConfig, secondHc: HCConfig): boolean {
    // Empty hcId values are ignored because several local configs are filled from environment variables later.
    return (
      firstHc.config.hcParam === secondHc.config.hcParam ||
      (!!firstHc.hcId && !!secondHc.hcId && firstHc.hcId === secondHc.hcId)
    );
  }
}
